import { useEffect, useState } from "react";
import { api, formatCents, formatDate, formatDateTime } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select, Textarea } from "@/components/ui/Primitives";
import { Hammer, Plus, Calendar, DollarSign, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  pending: "amber", scheduled: "blue", in_progress: "violet",
  completed: "green", cancelled: "slate"
};

export default function WorkOrders() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vendor_id: "", property_id: "", title: "", description: "",
    scheduled_at: "", estimated_cost: ""
  });

  const load = async () => {
    setLoading(true);
    try {
      const [w, v, p] = await Promise.all([
        api.get("/work-orders"),
        api.get("/my-vendors"),
        api.get("/properties"),
      ]);
      setItems(Array.isArray(w.data) ? w.data : (w.data?.work_orders || []));
      setVendors(Array.isArray(v.data) ? v.data : (v.data?.vendors || []));
      setProperties(Array.isArray(p.data) ? p.data : (p.data?.properties || []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/work-orders", {
        vendor_id: form.vendor_id,
        property_id: form.property_id,
        title: form.title.trim(),
        description: form.description.trim(),
        scheduled_at: form.scheduled_at || null,
        estimated_cost_cents: form.estimated_cost ? Math.round(Number(form.estimated_cost) * 100) : null,
      });
      toast.success("Work order created");
      setOpen(false);
      setForm({ vendor_id: "", property_id: "", title: "", description: "", scheduled_at: "", estimated_cost: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create work order");
    } finally { setSaving(false); }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Work orders"
        subtitle="Assign maintenance to vendors and track completion."
        actions={
          <Button onClick={() => setOpen(true)} disabled={vendors.length === 0} data-testid="new-work-order-button">
            <Plus className="w-4 h-4"/> New work order
          </Button>
        }
      />

      {vendors.length === 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200 mb-5">
          <div className="text-sm text-amber-900">
            <b>Add a vendor first.</b> You need at least one vendor before creating work orders. Go to <a href="/vendors" className="font-semibold underline">Vendors</a>.
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState
          icon={Hammer}
          title="No work orders yet"
          description="Create a work order to dispatch a vendor to one of your properties."
          action={null}
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 font-semibold">Title</th>
                  <th className="px-5 py-3 font-semibold">Vendor</th>
                  <th className="px-5 py-3 font-semibold">Scheduled</th>
                  <th className="px-5 py-3 font-semibold text-right">Est. cost</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((w) => {
                  const vendor = vendors.find((v) => v.vendor_id === w.vendor_id);
                  return (
                    <tr key={w.work_order_id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`work-order-row-${w.work_order_id}`}>
                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-900">{w.title}</div>
                        <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">{w.description}</div>
                      </td>
                      <td className="px-5 py-4 text-slate-700">{vendor?.name || w.vendor_id?.slice(-6) || "—"}</td>
                      <td className="px-5 py-4 text-slate-700">{w.scheduled_at ? formatDateTime(w.scheduled_at) : "—"}</td>
                      <td className="px-5 py-4 text-right text-slate-700">{w.estimated_cost_cents ? formatCents(w.estimated_cost_cents) : "—"}</td>
                      <td className="px-5 py-4"><Pill tone={STATUS_TONE[w.status] || "slate"}>{w.status?.replace("_"," ")}</Pill></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New work order" testid="work-order-modal">
        <form onSubmit={submit} className="space-y-4">
          <Select label="Vendor" required value={form.vendor_id} onChange={(e)=>setForm({...form, vendor_id:e.target.value})} testid="wo-vendor-select">
            <option value="">Select a vendor</option>
            {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{`${v.name} · ${(v.trade || "").replace(/_/g," ")}`}</option>)}
          </Select>
          <Select label="Property" required value={form.property_id} onChange={(e)=>setForm({...form, property_id:e.target.value})} testid="wo-property-select">
            <option value="">Select a property</option>
            {properties.map((p) => <option key={p.property_id} value={p.property_id}>{`${p.name}`}</option>)}
          </Select>
          <Input label="Title" required value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} testid="wo-title-input"/>
          <Textarea label="Description" required value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} testid="wo-description-input"/>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Scheduled" type="datetime-local" value={form.scheduled_at} onChange={(e)=>setForm({...form, scheduled_at:e.target.value})}/>
            <Input label="Est. cost (CAD)" type="number" step="0.01" value={form.estimated_cost} onChange={(e)=>setForm({...form, estimated_cost:e.target.value})}/>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="wo-create-submit">{saving ? "Creating…" : "Create work order"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
